import { AfterContentInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { CredentialsService } from '@app/core';
import { User, UserInput } from '@app/models';
import { ErrorHandlerService, NotyService, UserService } from '@app/services';
import { ConfirmOtpModalService } from '@app/shared';

export type ModalResult = void;
export interface ModalPublicProperties {
  user: User;
}
export enum ModalStep {
  email,
  password
}

@Component({
  selector: 'app-edit-email-modal',
  templateUrl: './edit-email-modal.component.html',
  styleUrls: ['./edit-email-modal.component.scss'],
  standalone: false
})
export class EditEmailModalComponent implements ModalPublicProperties, AfterContentInit {
  @Input() user: User;
  form!: FormGroup;
  currentStep = ModalStep.email;
  isLoading = false;
  skipPassword = false;
  readonly ModalStep = ModalStep;
  @ViewChild('emailInput', { static: false }) private emailInput: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInput', { static: false }) private passwordInput: ElementRef<HTMLInputElement>;

  constructor(
    public modal: NgbActiveModal,
    public translateService: TranslateService,
    private formBuilder: FormBuilder,
    private notyService: NotyService,
    private errorHandlerService: ErrorHandlerService,
    private userService: UserService,
    private credentialsService: CredentialsService,
    private confirmOtpModalService: ConfirmOtpModalService
  ) {
    this.form = this.formBuilder.group({
      otp: '',
      user: this.formBuilder.group({
        email: '',
        password: '',
        name: null,
        birthday: null
      })
    } as UserInput);
  }

  ngAfterContentInit() {
    if (!this.user?.email || !this.user?.email_verified_at) {
      this.notyService.error(this.translateService.instant('error_email_unverified'));
      this.modal.dismiss();
    } else {
      this.skipPassword = !!this.user.organization?.parent;
      this.form.setValue({
        user: {
          email: this.user.email,
          password: '',
          name: this.user.name, // 'name' is required to run the updateUser mutation
          birthday: this.user.birthday // 'birthday' is required to run the updateUser mutation
        } as UserInput,
        otp: ''
      });

      this.setInputFocus();
    }
  }

  next() {
    this.form.get('user.email').setValue((this.form.get('user.email').value || '').trim());

    if (this.form.get('user.email').value !== this.user.email) {
      if (!this.skipPassword && this.currentStep === ModalStep.email) {
        this.currentStep = ModalStep.password;
        this.setInputFocus();
      } else {
        this.updateEmail();
      }
    } else {
      this.modal.close();
    }
  }

  private updateEmail() {
    this.isLoading = true;
    this.form.markAllAsTouched();

    const formValue = this.form.value;
    if (this.skipPassword) {
      delete formValue.user.password;
    }

    (!this.skipPassword && this.credentialsService.credentials.mfa.length > 0 ? this.confirmOtpModalService.open() : of(null))
      .pipe(
        tap(otp => (formValue.otp = otp)),
        switchMap(() => this.userService.updateCurrentUser(formValue)),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () => {
          if (formValue.user.email && this.user.email !== formValue.user.email) {
            this.notyService.success(this.translateService.instant('notyService.emailConfirmationSent', { userEmail: formValue.user.email }));
          }
          this.modal.close();
        },
        error: error => this.errorHandlerService.handleValidation(this.form, error)
      });
  }

  private setInputFocus() {
    setTimeout(() => (this.currentStep === ModalStep.password ? this.passwordInput : this.emailInput).nativeElement.focus());
  }
}
